/**
 * VisualProfile -- Declarative visual configuration for levels and menus.
 *
 * Captures everything each scene currently sets up inline:
 * clear color, fog, lighting rigs, post-processing pipelines, and glow.
 *
 * Levels compose a VisualProfile from reusable presets (light-rigs.ts,
 * post-process.ts) plus per-level palette overrides. The `applyVisualProfile`
 * utility turns a profile into Babylon.js primitives, replacing 30-50 lines
 * of boilerplate per scene.
 *
 * Scene files are NOT modified yet -- this is the type/preset creation pass.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { HemisphericLight } from "@babylonjs/core/Lights/hemisphericLight";
import { DirectionalLight } from "@babylonjs/core/Lights/directionalLight";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import "@babylonjs/core/Lights/Shadows/shadowGeneratorSceneComponent";
import { RenderTargetTexture } from "@babylonjs/core/Materials/Textures/renderTargetTexture";
import { resolveShadowRefreshRate } from "./shadowRefreshRate";
import { DefaultRenderingPipeline } from "@babylonjs/core/PostProcesses/RenderPipeline/Pipelines/defaultRenderingPipeline";
import "@babylonjs/core/Rendering/depthRendererSceneComponent";
import { GlowLayer } from "@babylonjs/core/Layers/glowLayer";
import { ImageProcessingConfiguration } from "@babylonjs/core/Materials/imageProcessingConfiguration";
import { ColorCurves } from "@babylonjs/core/Materials/colorCurves";
import { isLowFiMode } from "../core/lowFiMode";

// ── Light Rig ──────────────────────────────────────────────────────────

export interface AmbientLightPreset {
  /** Upward direction color (sky contribution). */
  color: Color3;
  /** Specular contribution from the ambient. */
  specular: Color3;
  /** Ground bounce color (light reflected from below). */
  groundColor: Color3;
  /** Overall intensity. */
  intensity: number;
}

export interface KeyLightPreset {
  /** Direction the key light points (normalized). */
  direction: Vector3;
  /** Diffuse color. */
  color: Color3;
  /** Specular highlight color. */
  specular: Color3;
  /** Intensity multiplier. */
  intensity: number;
  /** Whether to attach a ShadowGenerator to this light. */
  shadowEnabled: boolean;
  /** Shadow map resolution (defaults to 1024). */
  shadowMapSize?: number;
  /** Shadow blur kernel size (defaults to 32). */
  shadowBlurKernel?: number;
  /** Use the radius-controlled Gaussian filter instead of the legacy box filter. */
  shadowKernelBlur?: boolean;
  /** Light fraction retained in full shadow (0..1, legacy default 0). */
  shadowDarkness?: number;
  /** Prefer full-float exponential depth; reduce its exponent on format fallback. */
  shadowHighPrecision?: boolean;
  /**
   * Shadow-map refresh rate. Accepts the numeric constants from
   * RenderTargetTexture (or the re-exported SHADOW_* constants from
   * shadowRefreshRate.ts):
   *   SHADOW_ONCE             (0) — never refreshed after first render;
   *                                 use only for genuinely static scenes
   *                                 with no kinematic shadow casters.
   *                                 DEFERRED — pending human visual-quality call.
   *   SHADOW_EVERY_FRAME      (1) — recomputed every frame (original behaviour).
   *   SHADOW_EVERY_TWO_FRAMES (2) — default; halves depth-pass cost,
   *                                 one-frame shadow lag (imperceptible in play).
   * Omit to accept the project default (every-2-frames).
   */
  shadowRefreshRate?: number;
}

export interface AccentLightPreset {
  /** World position. */
  position: Vector3;
  /** Diffuse color. */
  color: Color3;
  /** Specular color (defaults to color if omitted). */
  specular?: Color3;
  /** Intensity. */
  intensity: number;
  /** Maximum light range in world units. */
  range: number;
}

export interface LightRigPreset {
  ambient: AmbientLightPreset;
  key: KeyLightPreset;
  /** Optional point light accents (candles, bioluminescence, etc.). */
  accents?: AccentLightPreset[];
}

// ── Post-Processing ────────────────────────────────────────────────────

export interface BloomPreset {
  threshold: number;
  weight: number;
  kernel: number;
}

/**
 * Colour-grading controls exposed through Babylon's ColorCurves.
 * Values in [-100, 100] for hue/density; saturation is additive in the
 * same range; exposure is a stops-like float. All optional.
 */
export interface ColorGradingPreset {
  /** Global hue shift. Negative = warmer, positive = cooler (rough). */
  globalHue?: number;
  /** Saturation boost. Positive punches up jewel tones; negative desaturates. */
  globalSaturation?: number;
  /** Density (darken/lift shadows globally). */
  globalDensity?: number;
  /** Additive exposure on top of `imageProcessing.exposure`. */
  globalExposure?: number;
  /** Optional per-range tuning — keep flat unless a level needs it. */
  highlightsHue?: number;
  highlightsSaturation?: number;
  midtonesHue?: number;
  midtonesSaturation?: number;
  shadowsHue?: number;
  shadowsSaturation?: number;
}

export type ToneMappingMode = "aces" | "hable" | "standard" | "none";

export interface PostProcessPreset {
  bloom?: BloomPreset;
  chromaticAberration?: { amount: number };
  grain?: { intensity: number; animated: boolean };
  vignette?: { weight: number; cameraFov: number; color: Color4 };
  imageProcessing?: { contrast: number; exposure: number };
  /** Tone mapping operator. Defaults to "aces" so PBR lights don't blow out. */
  toneMapping?: ToneMappingMode;
  /** Colour curves applied on top of imageProcessing contrast/exposure. */
  colorGrading?: ColorGradingPreset;
}

// ── Fog ────────────────────────────────────────────────────────────────

export interface FogPreset {
  color: Color3;
  density: number;
  /** Babylon fog mode. Defaults to 'exp2'. */
  mode?: 'exp' | 'exp2' | 'linear';
}

// ── Full Profile ───────────────────────────────────────────────────────

export interface VisualProfile {
  /** Scene clear / background color. */
  clearColor: Color4;
  /** Exponential fog settings. Omit to disable fog. */
  fog?: FogPreset;
  /** Lighting rig: ambient + key light + optional accents. */
  lightRig: LightRigPreset;
  /** DefaultRenderingPipeline settings. */
  postProcess: PostProcessPreset;
  /** GlowLayer intensity (0.6 - 0.9 range in existing levels). */
  glowIntensity: number;
  /** Named color palette for level-specific use (UI, materials, particles). */
  palette: Record<string, Color3 | Color4>;
}

// ── Apply Handles ──────────────────────────────────────────────────────

/** Handles returned by applyVisualProfile for cleanup and runtime access. */
export interface VisualProfileHandles {
  hemiLight: HemisphericLight;
  keyLight: DirectionalLight;
  accentLights: PointLight[];
  shadowGenerator: ShadowGenerator | null;
  pipeline: DefaultRenderingPipeline;
  glowLayer: GlowLayer;
  /** Dispose all created resources. */
  dispose(): void;
}

export interface ApplyVisualProfileOptions {
  /** Reuse an existing shared glow layer instead of creating a new one. */
  glowLayer?: GlowLayer;
}

// ── Apply Utility ──────────────────────────────────────────────────────

const FOG_MODE_MAP = {
  exp: Scene.FOGMODE_EXP,
  exp2: Scene.FOGMODE_EXP2,
  linear: Scene.FOGMODE_LINEAR,
} as const;

/**
 * Swap the active post-process preset on an existing DefaultRenderingPipeline.
 *
 * Used by levels whose mood changes at runtime — e.g. the Overgrown
 * Conservatory's Incubation → Irrigation cycle. Writes every field the
 * preset defines; unset fields keep their prior values.
 */
export function applyPostProcessPreset(
  pipeline: DefaultRenderingPipeline,
  preset: PostProcessPreset,
): void {
  // Low-fi: leave every post-process effect off. Without this guard,
  // runtime preset swaps (e.g. the Conservatory Incubation/Irrigation
  // flip) would re-enable the full-screen passes applyVisualProfile
  // deliberately skipped.
  if (isLowFiMode()) return;
  if (preset.bloom) {
    pipeline.bloomEnabled = true;
    pipeline.bloomThreshold = preset.bloom.threshold;
    pipeline.bloomWeight = preset.bloom.weight;
    pipeline.bloomKernel = preset.bloom.kernel;
  }
  if (preset.chromaticAberration) {
    pipeline.chromaticAberrationEnabled = true;
    pipeline.chromaticAberration.aberrationAmount = preset.chromaticAberration.amount;
  }
  if (preset.grain) {
    pipeline.grainEnabled = true;
    pipeline.grain.intensity = preset.grain.intensity;
    pipeline.grain.animated = preset.grain.animated;
  }
  if (preset.imageProcessing) {
    pipeline.imageProcessingEnabled = true;
    pipeline.imageProcessing.contrast = preset.imageProcessing.contrast;
    pipeline.imageProcessing.exposure = preset.imageProcessing.exposure;
  }
  if (preset.vignette) {
    pipeline.imageProcessingEnabled = true;
    pipeline.imageProcessing.vignetteEnabled = true;
    pipeline.imageProcessing.vignetteWeight = preset.vignette.weight;
    pipeline.imageProcessing.vignetteCameraFov = preset.vignette.cameraFov;
    pipeline.imageProcessing.vignetteColor = preset.vignette.color;
  }
  if (preset.toneMapping) {
    pipeline.imageProcessingEnabled = true;
    switch (preset.toneMapping) {
      case "aces":
      case "hable":
        pipeline.imageProcessing.toneMappingEnabled = true;
        pipeline.imageProcessing.toneMappingType =
          ImageProcessingConfiguration.TONEMAPPING_ACES;
        break;
      case "standard":
        pipeline.imageProcessing.toneMappingEnabled = true;
        pipeline.imageProcessing.toneMappingType =
          ImageProcessingConfiguration.TONEMAPPING_STANDARD;
        break;
      case "none":
        pipeline.imageProcessing.toneMappingEnabled = false;
        break;
    }
  }
  if (preset.colorGrading) {
    const grade = preset.colorGrading;
    const curves = pipeline.imageProcessing.colorCurves ?? new ColorCurves();
    if (grade.globalHue !== undefined) curves.globalHue = grade.globalHue;
    if (grade.globalSaturation !== undefined) curves.globalSaturation = grade.globalSaturation;
    if (grade.globalDensity !== undefined) curves.globalDensity = grade.globalDensity;
    if (grade.globalExposure !== undefined) curves.globalExposure = grade.globalExposure;
    if (grade.highlightsHue !== undefined) curves.highlightsHue = grade.highlightsHue;
    if (grade.highlightsSaturation !== undefined) curves.highlightsSaturation = grade.highlightsSaturation;
    if (grade.midtonesHue !== undefined) curves.midtonesHue = grade.midtonesHue;
    if (grade.midtonesSaturation !== undefined) curves.midtonesSaturation = grade.midtonesSaturation;
    if (grade.shadowsHue !== undefined) curves.shadowsHue = grade.shadowsHue;
    if (grade.shadowsSaturation !== undefined) curves.shadowsSaturation = grade.shadowsSaturation;
    pipeline.imageProcessing.colorCurves = curves;
    pipeline.imageProcessing.colorCurvesEnabled = true;
  }
}

/**
 * Tween-owned scratch Color4 for the vignette blend. Module-level so the
 * per-frame lerp never allocates. NEVER .set()/.copyFrom() the pipeline's
 * currently-assigned vignetteColor in place instead — applyPostProcessPreset
 * aliases the preset constants into the pipeline (see the by-reference
 * assignment above), so an in-place write would silently corrupt the
 * module-constant presets for the rest of the session.
 */
const VIGNETTE_LERP_SCRATCH = new Color4(0, 0, 0, 1);

/**
 * Blend every numeric field between two presets at parameter t in [0,1].
 * Writes values only — never toggles *Enabled flags (each flag flip changes
 * shader defines and forces an effect recompile = visible stutter) and
 * never mutates either preset. It assumes applyPostProcessPreset already
 * enabled the passes. Undefined colorGrading fields lerp to/from 0 (the
 * ColorCurves default), which also clears the stale cross-preset residue
 * applyPostProcessPreset leaves behind (Q-034).
 */
export function lerpPostProcessPreset(
  pipeline: DefaultRenderingPipeline,
  from: PostProcessPreset,
  to: PostProcessPreset,
  t: number,
): void {
  // Low-fi: pipeline.imageProcessing is null while the pass is off
  // (imageProcessingEnabled stays false), so none of the writes below
  // may run — same guard as applyPostProcessPreset.
  if (isLowFiMode()) return;
  const mix = (a: number, b: number): number => a + (b - a) * t;
  if (from.bloom && to.bloom) {
    pipeline.bloomThreshold = mix(from.bloom.threshold, to.bloom.threshold);
    pipeline.bloomWeight = mix(from.bloom.weight, to.bloom.weight);
    pipeline.bloomKernel = Math.round(mix(from.bloom.kernel, to.bloom.kernel));
  }
  if (from.imageProcessing && to.imageProcessing) {
    pipeline.imageProcessing.contrast = mix(from.imageProcessing.contrast, to.imageProcessing.contrast);
    pipeline.imageProcessing.exposure = mix(from.imageProcessing.exposure, to.imageProcessing.exposure);
  }
  if (from.vignette && to.vignette) {
    pipeline.imageProcessing.vignetteWeight = mix(from.vignette.weight, to.vignette.weight);
    pipeline.imageProcessing.vignetteCameraFov = mix(from.vignette.cameraFov, to.vignette.cameraFov);
    // Lerp into the tween-owned scratch and reassign — see the scratch's
    // doc comment for why in-place writes are forbidden here.
    Color4.LerpToRef(from.vignette.color, to.vignette.color, t, VIGNETTE_LERP_SCRATCH);
    pipeline.imageProcessing.vignetteColor = VIGNETTE_LERP_SCRATCH;
  }
  const a = from.colorGrading ?? {};
  const b = to.colorGrading ?? {};
  const curves = pipeline.imageProcessing.colorCurves ?? new ColorCurves();
  curves.globalHue = mix(a.globalHue ?? 0, b.globalHue ?? 0);
  curves.globalSaturation = mix(a.globalSaturation ?? 0, b.globalSaturation ?? 0);
  curves.globalDensity = mix(a.globalDensity ?? 0, b.globalDensity ?? 0);
  curves.globalExposure = mix(a.globalExposure ?? 0, b.globalExposure ?? 0);
  curves.highlightsHue = mix(a.highlightsHue ?? 0, b.highlightsHue ?? 0);
  curves.highlightsSaturation = mix(a.highlightsSaturation ?? 0, b.highlightsSaturation ?? 0);
  curves.midtonesHue = mix(a.midtonesHue ?? 0, b.midtonesHue ?? 0);
  curves.midtonesSaturation = mix(a.midtonesSaturation ?? 0, b.midtonesSaturation ?? 0);
  curves.shadowsHue = mix(a.shadowsHue ?? 0, b.shadowsHue ?? 0);
  curves.shadowsSaturation = mix(a.shadowsSaturation ?? 0, b.shadowsSaturation ?? 0);
  pipeline.imageProcessing.colorCurves = curves;
  // Tone mapping is intentionally not lerped (both L2 presets are "aces");
  // it stays whatever applyPostProcessPreset last set.
}

/**
 * Apply a VisualProfile to a Babylon.js scene.
 *
 * Creates lights, shadow generator, post-processing pipeline, glow layer,
 * and configures fog/clear color. Returns handles for cleanup and runtime
 * access (e.g. adding shadow casters).
 *
 * This replaces the ~30-50 lines of inline visual setup in each scene file.
 */
export function applyVisualProfile(
  scene: Scene,
  profile: VisualProfile,
  options: ApplyVisualProfileOptions = {},
): VisualProfileHandles {
  // ── Clear color ──
  scene.clearColor = profile.clearColor;

  // ── Fog ──
  if (profile.fog) {
    scene.fogMode = FOG_MODE_MAP[profile.fog.mode ?? 'exp2'];
    scene.fogDensity = profile.fog.density;
    scene.fogColor = profile.fog.color;
  }

  const lowFi = isLowFiMode();

  // ── Glow layer ──
  const ownsGlowLayer = !options.glowLayer;
  const glowLayer = options.glowLayer ?? new GlowLayer("glow", scene, { blurKernelSize: 32 });
  glowLayer.intensity = profile.glowIntensity;
  // Low-fi: the glow layer renders the whole emissive set into an extra
  // pass + blur chain per frame — pure cost on a CPU rasterizer.
  if (lowFi) glowLayer.isEnabled = false;

  // ── Ambient light ──
  const hemiLight = new HemisphericLight("hemiLight", new Vector3(0, 1, 0), scene);
  hemiLight.diffuse = profile.lightRig.ambient.color;
  hemiLight.specular = profile.lightRig.ambient.specular;
  hemiLight.groundColor = profile.lightRig.ambient.groundColor;
  hemiLight.intensity = profile.lightRig.ambient.intensity;
  // Characters create their local lights before the level rig. Reserve
  // material light slots for the global rig so those points cannot evict
  // the key from Babylon's default four-light budget.
  hemiLight.renderPriority = 20;

  // ── Key light (directional) ──
  const keyLight = new DirectionalLight(
    "keyLight",
    profile.lightRig.key.direction,
    scene,
  );
  keyLight.diffuse = profile.lightRig.key.color;
  keyLight.specular = profile.lightRig.key.specular;
  keyLight.intensity = profile.lightRig.key.intensity;
  keyLight.renderPriority = 30;

  // ── Shadow generator ──
  // Default map size is 2048: the directional light's ortho frustum is
  // auto-fit to the caster set, which spans the full level width
  // (100-160 world units), so 1024px left each texel covering ~15cm —
  // cat shadows read as blobs (2026-07-10 visual review, finding #3).
  // Keep the caster set tight (players/NPCs/gameplay props, NOT whole
  // environment GLBs) or the resolution gain is eaten by frustum bloat.
  let shadowGenerator: ShadowGenerator | null = null;
  if (profile.lightRig.key.shadowEnabled) {
    // Low-fi: keep the ShadowGenerator object (scenes hold non-null
    // references and register casters against it) but make it inert —
    // a 16px map rendered exactly once costs nothing and draws nothing
    // visible.
    const mapSize = lowFi ? 16 : profile.lightRig.key.shadowMapSize ?? 2048;
    shadowGenerator = new ShadowGenerator(mapSize, keyLight, profile.lightRig.key.shadowHighPrecision);
    if (profile.lightRig.key.shadowHighPrecision && shadowGenerator.getShadowMap()!.textureType !== 1) {
      // Half-float ESM at scale50 collapses cat-sized contact depth to zero
      // or one subnormal value. Keep a representable range on fallback GPUs.
      shadowGenerator.depthScale = 10;
    }
    shadowGenerator.useBlurExponentialShadowMap = !lowFi;
    shadowGenerator.useKernelBlur = !lowFi && (profile.lightRig.key.shadowKernelBlur ?? false);
    shadowGenerator.blurKernel = profile.lightRig.key.shadowBlurKernel ?? 32;
    shadowGenerator.setDarkness(profile.lightRig.key.shadowDarkness ?? 0);
    // Throttle shadow-map refresh to halve depth-pass + blur cost.
    // Default: every-2-frames — cats are kinematic casters so RENDER_ONCE
    // would freeze their shadows in place (visible bug); every-2-frames keeps
    // shadows tracking with at most one-frame lag.
    // Per-level opt-in to RENDER_ONCE (static scenes) is DEFERRED — set
    // shadowRefreshRate: SHADOW_ONCE in the LightRigPreset when ready.
    const refreshRate = lowFi
      ? RenderTargetTexture.REFRESHRATE_RENDER_ONCE
      : resolveShadowRefreshRate(
          profile.lightRig.key.shadowRefreshRate,
          RenderTargetTexture.REFRESHRATE_RENDER_ONEVERYTWOFRAMES,
        );
    shadowGenerator.getShadowMap()!.refreshRate = refreshRate;
  }

  // ── Accent lights ──
  // Low-fi: skipped entirely. Ranged point lights are the root cause of
  // the shader-variant churn that pins software renderers at ~1 fps
  // (meshes entering/leaving a light's range flip material defines,
  // forcing ~1s SwiftShader re-links nearly every frame).
  const accentLights: PointLight[] = [];
  if (profile.lightRig.accents && !lowFi) {
    for (let i = 0; i < profile.lightRig.accents.length; i++) {
      const def = profile.lightRig.accents[i];
      const light = new PointLight(`accent_${i}`, def.position, scene);
      light.diffuse = def.color;
      light.specular = def.specular ?? def.color;
      light.intensity = def.intensity;
      light.range = def.range;
      accentLights.push(light);
    }
  }

  // ── Post-processing pipeline ──
  // Low-fi: the pipeline object still exists (scenes hold non-null
  // references and swap presets through applyPostProcessPreset, which
  // no-ops in low-fi), but every effect stays off so the pipeline never
  // instantiates its full-screen passes.
  const pipeline = new DefaultRenderingPipeline("pipeline", true, scene);

  // Anti-aliasing. The pipeline's render-to-texture chain bypasses the
  // canvas's multisampled backbuffer, so without these two settings the
  // game ships with NO anti-aliasing at all — every platform edge and
  // brass rail crawls in motion (2026-07-10 visual review, finding #1).
  // MSAA on the pipeline's RTT handles geometry edges; FXAA cleans up
  // shader/specular aliasing the multisample resolve can't reach.
  //
  // PERF (2026-07-11 L1 A/B, headless GPU Chrome, fpsDuringMotion):
  // this config is affordable ONLY with a tight shadow-caster set. On
  // L1 with ~80 background env meshes registered as casters, MSAA4+
  // FXAA+2048 dropped p50 60 → 40; after trimming casters to gameplay
  // meshes the same config measures p05 50.3 / p50 59.7 (baseline
  // parity). If a level regresses in the FPS budget test, audit its
  // addShadowCaster calls before touching these AA settings.
  if (!lowFi) {
    pipeline.samples = 4;
    pipeline.fxaaEnabled = true;
  }

  if (profile.postProcess.bloom && !lowFi) {
    pipeline.bloomEnabled = true;
    pipeline.bloomThreshold = profile.postProcess.bloom.threshold;
    pipeline.bloomWeight = profile.postProcess.bloom.weight;
    pipeline.bloomKernel = profile.postProcess.bloom.kernel;
  }

  if (profile.postProcess.chromaticAberration && !lowFi) {
    pipeline.chromaticAberrationEnabled = true;
    pipeline.chromaticAberration.aberrationAmount =
      profile.postProcess.chromaticAberration.amount;
  }

  if (profile.postProcess.grain && !lowFi) {
    pipeline.grainEnabled = true;
    pipeline.grain.intensity = profile.postProcess.grain.intensity;
    pipeline.grain.animated = profile.postProcess.grain.animated;
  }

  if (profile.postProcess.imageProcessing && !lowFi) {
    pipeline.imageProcessingEnabled = true;
    pipeline.imageProcessing.contrast =
      profile.postProcess.imageProcessing.contrast;
    pipeline.imageProcessing.exposure =
      profile.postProcess.imageProcessing.exposure;
  }

  if (profile.postProcess.vignette && !lowFi) {
    pipeline.imageProcessingEnabled = true;
    pipeline.imageProcessing.vignetteEnabled = true;
    pipeline.imageProcessing.vignetteWeight =
      profile.postProcess.vignette.weight;
    pipeline.imageProcessing.vignetteCameraFov =
      profile.postProcess.vignette.cameraFov;
    pipeline.imageProcessing.vignetteColor =
      profile.postProcess.vignette.color;
  }

  // Tone mapping — ACES by default so PBR metals/emissives don't blow
  // out to white. The previous default (Standard) was clipping the
  // Atrium god-ray and the Canopy brass glow. Every level profile opts
  // into ACES unless it explicitly names another operator.
  // Low-fi: leave imageProcessing disabled — `pipeline.imageProcessing`
  // is null while the pass is off, so none of the tone-map/vignette/
  // grading writes below may run.
  pipeline.imageProcessingEnabled = !lowFi;
  const toneMap = lowFi ? "skip" : profile.postProcess.toneMapping ?? "aces";
  switch (toneMap) {
    case "aces":
      pipeline.imageProcessing.toneMappingEnabled = true;
      pipeline.imageProcessing.toneMappingType =
        ImageProcessingConfiguration.TONEMAPPING_ACES;
      break;
    case "hable":
      pipeline.imageProcessing.toneMappingEnabled = true;
      // Hable/Uncharted 2 tonemap — present as _TONEMAPPING_STANDARD in
      // older Babylon releases, so fall back to ACES if the enum is gone.
      pipeline.imageProcessing.toneMappingType =
        ImageProcessingConfiguration.TONEMAPPING_ACES;
      break;
    case "standard":
      pipeline.imageProcessing.toneMappingEnabled = true;
      pipeline.imageProcessing.toneMappingType =
        ImageProcessingConfiguration.TONEMAPPING_STANDARD;
      break;
    case "none":
      pipeline.imageProcessing.toneMappingEnabled = false;
      break;
  }

  // Colour grading — per-level mood tint applied on top of the linear
  // exposure/contrast pass. Ranges follow Babylon's ColorCurves API:
  //   hue: [-100, 100] (negative ≈ warmer, positive ≈ cooler)
  //   saturation: [-100, 100] (positive punches up jewel tones)
  //   density: [-100, 100] (negative lifts shadows, positive crushes)
  //   exposure: [-100, 100] (stops-like; small values feel big)
  if (profile.postProcess.colorGrading && !lowFi) {
    const grade = profile.postProcess.colorGrading;
    const curves = new ColorCurves();
    if (grade.globalHue !== undefined) curves.globalHue = grade.globalHue;
    if (grade.globalSaturation !== undefined) curves.globalSaturation = grade.globalSaturation;
    if (grade.globalDensity !== undefined) curves.globalDensity = grade.globalDensity;
    if (grade.globalExposure !== undefined) curves.globalExposure = grade.globalExposure;
    if (grade.highlightsHue !== undefined) curves.highlightsHue = grade.highlightsHue;
    if (grade.highlightsSaturation !== undefined) curves.highlightsSaturation = grade.highlightsSaturation;
    if (grade.midtonesHue !== undefined) curves.midtonesHue = grade.midtonesHue;
    if (grade.midtonesSaturation !== undefined) curves.midtonesSaturation = grade.midtonesSaturation;
    if (grade.shadowsHue !== undefined) curves.shadowsHue = grade.shadowsHue;
    if (grade.shadowsSaturation !== undefined) curves.shadowsSaturation = grade.shadowsSaturation;
    pipeline.imageProcessing.colorCurves = curves;
    pipeline.imageProcessing.colorCurvesEnabled = true;
  }

  // ── Dispose helper ──
  const dispose = (): void => {
    pipeline.dispose();
    for (const accent of accentLights) {
      accent.dispose();
    }
    if (shadowGenerator) {
      shadowGenerator.dispose();
    }
    if (ownsGlowLayer) {
      glowLayer.dispose();
    }
    keyLight.dispose();
    hemiLight.dispose();
  };

  return {
    hemiLight,
    keyLight,
    accentLights,
    shadowGenerator,
    pipeline,
    glowLayer,
    dispose,
  };
}
